<template>

  <div>
    <b-row
      class="content-header"
    >

      <!-- Content Left -->
      <b-col
        class="content-header-left mb-2"
        cols="7"
        md="9"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h2 class="custom-header-title float-left pr-1 mb-0">
              Retail Items
            </h2>
          </b-col>
        </b-row>
      </b-col>

      <!-- Content Right -->
      <b-col
        class="content-header-right text-right mb-1"
        md="3"
        cols="5"
      >
        <b-button
          v-if="canViewThisAction('create', 'RetailItem')"
          variant="success"
          class="mobile_create_button"
          :to="{ name: 'products-retail-items-create' }"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
            size="16"
          />
          <span class="align-middle d-md-block d-none">Create New Retail Item</span>
          <span class="align-middle d-md-none d-block">New</span>
        </b-button>
      </b-col>
    </b-row>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center mb-1 mb-md-0"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} - {{ dataMeta.to }} of {{ dataMeta.of }}</span>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <a
                href="javascript:void(0)"
                class="nav-link nav-link-search"
                @click="showSearchBar = !showSearchBar"
              >
                <feather-icon
                  icon="SearchIcon"
                  size="21"
                  class="mr-50"
                />
                <span class="align-middle">Search</span>
              </a>
              <b-input-group
                v-if="showSearchBar"
                class="input-group-merge mr-50"
              >
                <b-form-input
                  v-model="searchQuery"
                  placeholder="Search..."
                />
                <b-input-group-append is-text>
                  <feather-icon
                    icon="XIcon"
                    class="cursor-pointer"
                    @click="searchQuery = ''; showSearchBar = !showSearchBar"
                  />
                </b-input-group-append>
              </b-input-group>

              <b-dropdown
                id="dropdown-retail-item-filter"
                ref="filter_dropdown_retail_items"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                class="dropdown-modal"
                right
                variant="link"
              >
                <template #button-content>
                  <feather-icon
                    icon="FilterIcon"
                    size="21"
                    class="align-middle mr-50"
                  />
                  <span class="mr-1">Filters</span>
                </template>
                <retail-item-filter
                  :category.sync="categoryFilter"
                  :category-options="categoryOptions"
                  @emitHideFilter="hideFilter"
                />
              </b-dropdown>
            </div>
          </b-col>
        </b-row>
        <div v-if="categoryFilter.length">
          <hr class="dividerHR filter-divider">
          <b-row>
            <b-col
              cols="12"
              md="12"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <ul class="list-inline d-inline-block mb-1">
                <li
                  v-for="item in categoryFilter"
                  :key="item"
                  class="list-inline-item"
                >
                  <b-form-tag
                    class="mb-50"
                    variant="danger"
                    @remove="removeFromCategoryFilter(item)"
                  >
                    {{ categoryName(item) }}
                  </b-form-tag>
                </li>
                <li class="list-inline-item">
                  <span
                    class="text-danger clear-filter-text mb-50"
                    @click="clearAllFilters"
                  >
                    Clear Filters
                  </span>
                </li>
              </ul>
            </b-col>
          </b-row>
        </div>
      </div>

      <b-table
        ref="refRetailItemsTable"
        class="position-relative min-height-table"
        :items="itemListData"
        responsive
        :fields="tableColumns"
        primary-key="_id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        show-empty
        no-sort-reset
        empty-text="No retail items yet"
      >
        <template #cell(retailItem)="data">
          <div class="d-flex align-items-center">
            <b-img
              class="mr-1 img-preview-block"
              :src="data.item.image ? data.item.image : defaultImage"
            />
            <div>
              <div class="font-weight-bold">
                {{ data.item.name }}
              </div>
              <small class="text-muted">{{ data.item.sku }}</small>
            </div>
          </div>
        </template>

        <template #cell(category)="data">
          {{ data.item.categoryTitle || '–' }}
        </template>

        <template #cell(brand)="data">
          {{ data.item.brand || '–' }}
        </template>

        <template #cell(unitPrice)="data">
          {{ data.item.unitPrice !== undefined && data.item.unitPrice !== null ? `$${Number(data.item.unitPrice).toFixed(2)}` : '–' }}
        </template>

        <template #cell(createdAt)="data">
          {{ dateFormatWithTime(data.item.createdAt) }}
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="canViewThisAction('update', 'RetailItem')"
              :to="{ name: 'products-retail-items-edit', params: { id: data.item._id } }"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canViewThisAction('delete', 'RetailItem')"
              @click="deleteEntity(data.item._id)"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-card>

    <div
      v-if="totalRetailItems > perPage"
      class="mx-2 mb-2"
    >
      <b-row>
        <b-col
          cols="12"
          sm="12"
          class="d-flex align-items-center justify-content-center pagination-content"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRetailItems"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            align="center"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
              <span>Prev</span>
            </template>
            <template #next-text>
              <span>Next</span>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BCard, BTable, BDropdown, BDropdownItem, BFormInput,
  BInputGroup, BInputGroupAppend, BImg, BPagination, BFormTag,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import { useUtils as useAclUtils } from '@/libs/acl/custom'
// eslint-disable-next-line import/no-cycle
import useRetailItemList from './useRetailItemList'
import retailItemStoreModule from './retailItemStoreModule'
import RetailItemFilter from './RetailItemFilter.vue'

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BCard,
    BTable,
    BDropdown,
    BDropdownItem,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BImg,
    BPagination,
    BFormTag,
    RetailItemFilter,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },

  directives: {
    Ripple,
  },

  data() {
    return {
      // eslint-disable-next-line global-require
      defaultImage: require('@/assets/images/placeholder/placeholder_image.png'),
      showSearchBar: false,
      categoryOptions: [],
    }
  },

  mounted() {
    this.$http.get('inventory/category/respond-with/name-and-id')
      .then(response => {
        this.categoryOptions = response.data.categories ?? []
      })
      .catch(() => {})
  },

  methods: {
    hideFilter() {
      this.$refs.filter_dropdown_retail_items.hide(true)
    },
    categoryName(id) {
      const match = this.categoryOptions.find(option => option._id === id)
      return match ? match.name : id
    },
    removeFromCategoryFilter(item) {
      const index = this.categoryFilter.indexOf(item)
      if (index > -1) {
        this.categoryFilter.splice(index, 1)
      }
    },
    clearAllFilters() {
      this.categoryFilter = []
    },
    deleteEntity(id) {
      this.$swal({
        title: 'Delete this Retail Item?',
        html: '<p class="mt-1" />Are you sure you want to delete this<br>Retail Item? This cannot be undone.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/delete-yellow.png'),
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'No, go back',
        customClass: {
          confirmButton: 'btn btn-primary ml-1',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
      })
        .then(result => {
          if (result.value) {
            this.toDeletedID = id
            this.deleteRetailItem()
          }
        })
    },
  },

  setup() {
    const RETAIL_ITEM_APP_STORE_MODULE_NAME = 'app-product-retail-item'

    // Register module
    if (!store.hasModule(RETAIL_ITEM_APP_STORE_MODULE_NAME)) store.registerModule(RETAIL_ITEM_APP_STORE_MODULE_NAME, retailItemStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(RETAIL_ITEM_APP_STORE_MODULE_NAME)) store.unregisterModule(RETAIL_ITEM_APP_STORE_MODULE_NAME)
    })

    const { canViewThisAction } = useAclUtils()

    const {
      fetchRetailItems,
      deleteRetailItem,
      tableColumns,
      perPage,
      currentPage,
      totalRetailItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refRetailItemsTable,
      refetchData,
      categoryFilter,
      currentDataLength,
      itemListData,
      toDeletedID,
    } = useRetailItemList()

    return {
      fetchRetailItems,
      deleteRetailItem,
      tableColumns,
      perPage,
      currentPage,
      totalRetailItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refRetailItemsTable,
      refetchData,
      categoryFilter,
      currentDataLength,
      itemListData,
      toDeletedID,

      canViewThisAction,
    }
  },
}
</script>

<style lang="scss" scoped>
.img-preview-block {
  box-sizing: border-box;
  width: 38px;
  height: 38px;
  border: 1px solid #D2DCEA;
  border-radius: 5px;
  object-fit: cover;
}
</style>
